import { Component, ChangeDetectorRef, Inject, OnInit } from '@angular/core';

import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { forkJoin, Observable } from 'rxjs';

import { LucideAngularModule, Trash2, Settings2, Search, ChevronRight } from "lucide-angular";
import { PicklistService } from '../../services/picklist.service';

export interface PicklistEditModalData {
  kbId: number;
  picklists: {
        types: any[];
        items: { [typeId: number]: any[] };
    };
}

@Component({
    selector: 'app-picklist-edit-modal',
    standalone: true,
    imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatSnackBarModule,
    LucideAngularModule
],
    templateUrl: './picklist-edit-modal.component.html',
    styleUrls: ['./picklist-edit-modal.component.scss']
})
export class PicklistEditModalComponent implements OnInit {
    picklistForm: FormGroup | null = null;
    saving: boolean = false;
    loading: boolean = false;
    selectedTypeId: number | null = null;
    selectedTypeName: string = '';
    originalItems: { [index: number]: any } = {}; // Para rastrear valores originales
    searchQuery = '';
    filteredItems: Array<{ control: any; index: number }> = [];

    // icons
    readonly Trash2 = Trash2;
    readonly Settings2 = Settings2;
    readonly ChevronRight = ChevronRight;
    readonly Search = Search;

    constructor(
        public dialogRef: MatDialogRef<PicklistEditModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PicklistEditModalData,
        private fb: FormBuilder,
        private picklistService: PicklistService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        this.picklistForm = this.fb.group({
            selectedType: ['', Validators.required],
            items: this.fb.array([])
        });
    }

    // FormArray para los items dinámicos
    get itemsFormArray(): FormArray {
        return this.picklistForm?.get('items') as FormArray;
    }

    // Getter para verificar si se puede agregar items
    get canAddItems(): boolean {
        return !!this.selectedTypeId;
    }

    // Getter para verificar si el tipo seleccionado es de tipo identifier
    get isIdentifierType(): boolean {
        if (!this.selectedTypeId) return false;
        return this.data.picklists.types.find(t => t.picklistTypeId === this.selectedTypeId)?.isIdentifier === true;
    }

    // Manejar cambio de tipo seleccionado
    onTypeChange(typeId: number): void {
        this.selectedTypeId = typeId;
        // Buscar y guardar el nombre del tipo seleccionado
        const selectedType = this.data.picklists.types.find(t => t.picklistTypeId === typeId);
        this.selectedTypeName = selectedType?.name || '';

        // Limpiar items existentes y valores originales
        this.itemsFormArray.clear();
        this.originalItems = {};
        this.searchQuery = '';
        this.filteredItems = [];
        this.loading = true;

        // Cargar items directamente desde el backend al seleccionar el tipo
        this.picklistService.getPicklistItems(this.data.kbId, typeId, false).subscribe({
            next: (items) => {
                this.loading = false;
                (items || []).forEach(item => this.addExistingItem(item));
                this.updateFilteredItems();
                this.cdr.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cdr.detectChanges();
            }
        });
    }

    // Agregar item existente (nombre no editable, solo estado)
    private addExistingItem(item: any): void {
        const controls: any = {
            id: [item.picklistItemId],
            name: [{ value: item.name, disabled: true }], // Deshabilitar edición del nombre
            isActive: [item.isActive],
            isExisting: [true] // Flag para identificar items existentes
        };
        if (this.isIdentifierType) {
            controls['value'] = [{ value: item.value ?? '', disabled: true }];
        }
        const itemGroup = this.fb.group(controls);
        
        const index = this.itemsFormArray.length;
        // Guardar valores originales para detectar cambios
        this.originalItems[index] = {
            id: item.picklistItemId,
            name: item.name,
            isActive: item.isActive
        };
        
        this.itemsFormArray.push(itemGroup);
    }

    // Agregar nuevo item (editable)
    addNewItem(): void {
        if (!this.canAddItems) return;

        const controls: any = {
            id: [null],
            name: ['', Validators.required],
            isActive: [true],
            isExisting: [false] // Flag para identificar nuevos items
        };
        if (this.isIdentifierType) {
            controls['value'] = ['', Validators.required];
        }
        const itemGroup = this.fb.group(controls);
        this.itemsFormArray.push(itemGroup);
        this.updateFilteredItems();
    }

    // Eliminar item (solo nuevos items)
    removeItem(index: number): void {
        const item = this.itemsFormArray.at(index);
        const isExisting = item.get('isExisting')?.value;
        
        if (!isExisting) {
            this.itemsFormArray.removeAt(index);
            // Actualizar originalItems después de eliminar
            const newOriginalItems: { [index: number]: any } = {};
            Object.keys(this.originalItems).forEach(key => {
                const oldIndex = parseInt(key);
                if (oldIndex < index) {
                    newOriginalItems[oldIndex] = this.originalItems[oldIndex];
                } else if (oldIndex > index) {
                    newOriginalItems[oldIndex - 1] = this.originalItems[oldIndex];
                }
            });
            this.originalItems = newOriginalItems;
            this.updateFilteredItems();
        }
    }

    // Guardar cambios
    onSave(): void {
        if (!this.picklistForm?.valid || !this.selectedTypeId) {
            return;
        }

        const formValue = this.picklistForm.getRawValue();
        const newItems = this.getNewItems(formValue.items);
        const modifiedItems = this.getModifiedItems(formValue.items);

        if (newItems.length === 0 && modifiedItems.length === 0) {
            this.showError('No hay cambios para guardar.');
            return;
        }

        this.saving = true;
        this.picklistForm.get('selectedType')?.disable({ onlySelf: true });
        this.picklistForm.get('items')?.disable({ onlySelf: true });

        const requests = [
            ...newItems.map(item => this.buildCreateRequest(item)),
            ...modifiedItems.map(item => this.buildUpdateRequest(item))
        ];

        forkJoin(requests).subscribe({
            next: () => {
                this.saving = false;
                const total = newItems.length + modifiedItems.length;
                this.showSuccess(`${total} cambio(s) guardado(s) exitosamente.`);
                this.dialogRef.close({ success: true, count: total });
            },
            error: (error) => {
                this.saving = false;
                console.error('Error guardando cambios:', error);
                this.showError('Error al guardar los cambios. Por favor intente nuevamente.');
                this.picklistForm?.get('selectedType')?.enable({ onlySelf: true });
            }
        });
    }

    private getNewItems(items: any[]): any[] {
        return items.filter(item => !item.isExisting && item.name);
    }

    private getModifiedItems(items: any[]): any[] {
        return items.reduce((acc: any[], item: any, index: number) => {
            const original = this.originalItems[index];
            if (item.isExisting && original && (item.name !== original.name || item.isActive !== original.isActive)) {
                acc.push({ id: item.id, name: item.name, isActive: item.isActive });
            }
            return acc;
        }, []);
    }

    private buildCreateRequest(item: any): Observable<any> {
        const payload: any = { code: item.name.toUpperCase(), name: item.name, isActive: item.isActive ?? true };
        if (this.isIdentifierType && item.value) {
            payload['value'] = item.value;
        }
        return this.picklistService.createPicklistItem(this.data.kbId, this.selectedTypeId!, payload);
    }

    private buildUpdateRequest(item: any): Observable<any> {
        return this.picklistService.updatePicklistItem(this.data.kbId, this.selectedTypeId!, item.id, {
            name: item.name,
            isActive: item.isActive
        });
    }

    // Cancelar
    onCancel(): void {
        this.dialogRef.close(null);
    }

    // Mensajes de éxito y error
    private showSuccess(message: string): void {
        this.snackBar.open(message, 'Cerrar', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
        });
    }

    private showError(message: string): void {
        this.snackBar.open(message, 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
        });
    }

    // Verificar si un item es existente
    isExistingItem(index: number): boolean {
        return this.itemsFormArray.at(index).get('isExisting')?.value || false;
    }

    onSearchChange(value: string): void {
        this.searchQuery = value;
        this.updateFilteredItems();
    }

    // Actualizar items filtrados con su índice original
    private updateFilteredItems(): void {
        if (!this.searchQuery.trim()) {
            this.filteredItems = this.itemsFormArray.controls.map((control, index) => ({ control, index }));
            return;
        }

        const query = this.searchQuery.toLowerCase();
        const filtered: Array<{ control: any; index: number }> = [];
        
        this.itemsFormArray.controls.forEach((control: any, index: number) => {
            const name = control.get('name')?.value?.toLowerCase() || '';
            if (name.includes(query)) {
                filtered.push({ control, index });
            }
        });
        
        this.filteredItems = filtered;
    }

    // Verificar si hay cambios (nuevos items o modificaciones en items existentes)
    get hasChanges(): boolean {
        const formValue = this.picklistForm?.getRawValue();
        if (!formValue?.items) return false;

        // Verificar nuevos items
        const newItems = formValue.items.filter((item: any) => !item.isExisting && item.name);
        if (newItems.length > 0) return true;

        // Verificar items existentes que fueron modificados
        for (let index = 0; index < formValue.items.length; index++) {
            const item = formValue.items[index];
            if (item.isExisting && this.originalItems[index]) {
                const original = this.originalItems[index];
                if (item.name !== original.name || item.isActive !== original.isActive) {
                    return true;
                }
            }
        }

        return false;
    }
}